import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Address,
  BorrowerMessageKey,
  ConfirmMessageViewDocument,
  PortalSelfDocument,
} from '@willow/graphql-iso/src/portal';
import { addressToStringLines } from '@willow/shared-iso';
import { LoaderOverlay } from '@willow/shared-web';
import { CompanyId, LoanId } from '@willow/types-iso';

import { WillowModal } from '../modal/Modal';

import s from './ConfirmMailAddressModal.module.scss';

interface Props {
  companyId: CompanyId;
  loanId: LoanId;
  showModal: boolean;
  mailingAddress?: Address | undefined;
  myAccountPath: string;
}

export const ConfirmMailingAddressModal = ({ companyId, loanId, showModal, mailingAddress, myAccountPath }: Props) => {
  const history = useHistory();
  const [confirmMessageView, { loading: confirmMessageViewLoading }] = useMutation(ConfirmMessageViewDocument);

  const onSubmit = async () => {
    try {
      await confirmMessageView({
        variables: {
          companyId: companyId,
          loanId: loanId,
          messageKey: BorrowerMessageKey.MailingAddressConfirmedAt,
        },
        refetchQueries: [PortalSelfDocument],
      });
    } catch (_err: unknown) {
      toast.error('Something went wrong. Please try again.');
    }
  };

  const addressLines = mailingAddress ? addressToStringLines(mailingAddress) : undefined;

  return (
    <WillowModal showModal={showModal} onClose={onSubmit}>
      <div>
        <h2 className="mb-3 u-fs-3-header fw-bold">Is Your Mailing Address Correct?</h2>
        {mailingAddress ? (
          <>
            <p>
              Please review the mailing address we have on file. Your details are editable from the “My Account” page.
            </p>
            <p className="fw-bold mt-3">
              {addressLines?.line1}
              <br />
              {addressLines?.line2}
            </p>
          </>
        ) : (
          <>We do not have a mailing address on file, please add one from your “My Account” page. </>
        )}

        <div className="d-flex gap-2 mt-4 justify-content-end">
          <button
            type="button"
            className={s.btnOutline}
            onClick={async () => {
              await onSubmit();
              history.push(myAccountPath);
            }}
          >
            No, it is incorrect
          </button>
          <button
            type="button"
            className={s.btnPrimary}
            onClick={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            Yes, it is correct
          </button>
        </div>
      </div>
      <LoaderOverlay isLoading={confirmMessageViewLoading} />
    </WillowModal>
  );
};
